import type { MotherOrderLine } from '@api/mother-order';
import type { ProcurementOrder } from '@api/procurement-order';

/**
 * The inbound flow's small derivations, shared by the order detail, the shipment form and
 * the shipment lists so every surface reads a line or a load the same way.
 *
 * A "need" is the next thing a row is waiting on, said as one short chip. One rule, three
 * places: the same conditions drive the alerts on the server.
 */

export type NeedTone = 'muted' | 'info' | 'warn' | 'bad' | 'good';
export type Need = { label: string; tone: NeedTone; title?: string };

export const todayIso = (): string => new Date().toISOString().slice(0, 10);

const addDaysIso = (iso: string, days: number): string => {
    const d = new Date(`${iso}T00:00:00Z`);
    d.setUTCDate(d.getUTCDate() + days);
    return d.toISOString().slice(0, 10);
};

export const daysBetween = (fromIso: string, toIso: string): number =>
    Math.round(
        (new Date(`${toIso}T00:00:00Z`).getTime() - new Date(`${fromIso}T00:00:00Z`).getTime()) /
            86_400_000,
    );

/** Days before the ready date by which a forwarder should be booked. */
export const FORWARDER_LEAD_DAYS = 10;

// ─── Contract lines ─────────────────────────────────────────────

/**
 * A line whose QC is still to be recorded: it has a goods-ready date, nobody at the brand has
 * written a verdict, and units remain off any shipment. A shipment IS the verdict for the
 * units on it — a line wholly on shipments is never due, a partly shipped one is due for the
 * remainder, and a BRAND verdict settles the line whatever is shipped. Due from the moment
 * the date is set, not once it passes. The server's QC-due report applies the same rule.
 */
export const isQcDue = (
    line: Pick<MotherOrderLine, 'expectedReadinessDate' | 'qcSource' | 'shipped' | 'ordered'>,
): boolean =>
    !!line.expectedReadinessDate && line.qcSource !== 'BRAND' && line.shipped < line.ordered;

/** What a contract line is waiting on. */
export const lineNeed = (line: MotherOrderLine, today = todayIso()): Need => {
    if (line.ordered > 0 && line.shipped >= line.ordered) {
        // Every unit is on a shipment row; "Booked" until those rows leave the vendor.
        if (line.inProduction > 0) {
            return { label: 'Booked', tone: 'info', title: 'On a shipment, not yet dispatched' };
        }
        return { label: 'Shipped', tone: 'good' };
    }
    if (!line.expectedReadinessDate) return { label: 'Ready date', tone: 'warn', title: 'Set the goods-ready date for this line' };
    if (isQcDue(line)) {
        return { label: 'QC due', tone: 'warn', title: 'Record what passed and what was rejected' };
    }
    if (line.shippable > 0 && line.expectedReadinessDate <= today) {
        return { label: `Shippable ${line.shippable}`, tone: 'info' };
    }
    if (line.shippable <= 0) return { label: 'Nothing left', tone: 'muted' };
    return { label: `Ready ${fmtShortDate(line.expectedReadinessDate)}`, tone: 'muted' };
};

export const fmtShortDate = (iso: string): string => {
    const d = new Date(`${iso}T00:00:00Z`);
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' });
};

// ─── Shipments ──────────────────────────────────────────────────

type ShipmentLike = Pick<
    ProcurementOrder,
    | 'status'
    | 'readinessDate'
    | 'departureDate'
    | 'arrivalDate'
    | 'deliveryDate'
    | 'shippedAt'
    | 'arrivedAt'
    | 'deliveredAt'
    | 'forwarderVendorId'
    | 'manualReceivedAt'
    | 'cancelledAt'
>;

/**
 * What a shipment is waiting on, in order of urgency. Empty when it is received or cancelled.
 * The same four conditions the server's alerts fire on, so a red chip here and an alert on
 * Home are one fact seen twice.
 */
export const shipmentNeeds = (s: ShipmentLike, today = todayIso()): Need[] => {
    const needs: Need[] = [];
    if (s.cancelledAt || s.status === 'RECEIVED' || s.manualReceivedAt) return needs;
    const ready = s.readinessDate?.slice(0, 10) ?? null;
    const dep = s.departureDate?.slice(0, 10) ?? null;
    const arr = s.arrivalDate?.slice(0, 10) ?? null;

    if (!s.shippedAt) {
        if (!s.forwarderVendorId) {
            const soon = ready != null && daysBetween(today, ready) <= FORWARDER_LEAD_DAYS;
            needs.push({
                label: 'Forwarder',
                tone: soon ? 'bad' : 'warn',
                title: soon
                    ? `Goods ready ${ready ? fmtShortDate(ready) : ''}: book a forwarder now`
                    : 'No freight forwarder booked yet',
            });
        }
        if (!dep) {
            needs.push({ label: 'Dispatch date', tone: 'muted', title: 'Set the expected dispatch date' });
        } else if (dep < today) {
            needs.push({
                label: `Dispatch overdue ${daysBetween(dep, today)}d`,
                tone: 'bad',
                title: `Planned to leave ${fmtShortDate(dep)} and not marked dispatched`,
            });
        } else {
            needs.push({ label: `Dispatch ${fmtShortDate(dep)}`, tone: 'info', title: 'Mark dispatched when it leaves' });
        }
        return needs;
    }
    if (!s.arrivedAt) {
        if (arr && arr < today) {
            needs.push({
                label: `Arrival overdue ${daysBetween(arr, today)}d`,
                tone: 'bad',
                title: `Expected at destination ${fmtShortDate(arr)} and not marked arrived`,
            });
        } else {
            needs.push({ label: arr ? `Arrives ${fmtShortDate(arr)}` : 'In transit', tone: 'info', title: 'Mark arrived when it lands' });
        }
        return needs;
    }
    if (!s.deliveredAt) {
        needs.push({ label: 'Delivery', tone: 'info', title: 'Mark delivered when it reaches the warehouse' });
        return needs;
    }
    needs.push({ label: 'Receipt', tone: 'info', title: 'Received against a GRN, or mark received' });
    return needs;
};

// ─── Destination pickers ────────────────────────────────────────

/**
 * Where a vendor's shipment may go: our own (INTERNAL) warehouses, narrowed to the ones a
 * lane from the vendor reaches — a shipment without a lane has no expected dates. With no
 * lanes on file every internal warehouse is offered and `laneScoped` is false, so the picker
 * can say to add one. The warehouse already set stays listed, whatever the lanes say, so the
 * picker can still show it.
 */
export const destinationWarehouses = <W extends { warehouseId: string; type: string }>(
    warehouses: W[],
    lanes: Array<{ warehouseId: string }> | undefined,
    currentId?: string | null,
): { options: W[]; laneScoped: boolean } => {
    const internal = warehouses.filter((w) => w.type === 'INTERNAL');
    const reach = new Set((lanes ?? []).map((l) => l.warehouseId));
    const laneScoped = reach.size > 0;
    const options = laneScoped ? internal.filter((w) => reach.has(w.warehouseId)) : internal;
    if (currentId && !options.some((w) => w.warehouseId === currentId)) {
        const current = warehouses.find((w) => w.warehouseId === currentId);
        if (current) options.push(current);
    }
    return { options, laneScoped };
};

// ─── Suggested loads ────────────────────────────────────────────

export type SuggestedLoad = {
    key: string;
    /** The latest line ready date in the load: the earliest the load can be ready. */
    readyBy: string | null;
    qtys: Record<string, number>;
    units: number;
    cbm: number;
    /** Lines in the load with no CBM on file (counted in units, not volume). */
    uncounted: number;
    /** Under the lane's fill threshold; the nudge says what would fill it. */
    underFilled: boolean;
    nudge: string | null;
};

/**
 * Loads proposed from the shippable lines: sorted by ready date, packed one container at a
 * time against the cap, and cut whenever the next line would push the load's ready date more
 * than `windowDays` past the first line in it. Without a cap the only cut is the window.
 * Deterministic, so the strip reads the same after every refresh.
 */
export const suggestLoads = (
    lines: MotherOrderLine[],
    opts: { cap: number | null; minFillPct: number | null; windowDays?: number },
): SuggestedLoad[] => {
    const windowDays = opts.windowDays ?? 7;
    const pool = lines
        .filter((l) => l.shippable > 0)
        .map((l) => ({ line: l, left: l.shippable }))
        .sort((a, b) => {
            const ra = a.line.expectedReadinessDate ?? '9999-12-31';
            const rb = b.line.expectedReadinessDate ?? '9999-12-31';
            return ra < rb ? -1 : ra > rb ? 1 : 0;
        });
    const loads: SuggestedLoad[] = [];
    let current: SuggestedLoad | null = null;
    let windowStart: string | null = null;

    const open = (): SuggestedLoad => {
        current = { key: `L${loads.length + 1}`, readyBy: null, qtys: {}, units: 0, cbm: 0, uncounted: 0, underFilled: false, nudge: null };
        loads.push(current);
        windowStart = null;
        return current;
    };

    for (const entry of pool) {
        const ready = entry.line.expectedReadinessDate;
        const unitCbm = entry.line.volumeCbm != null && entry.line.volumeCbm > 0 ? entry.line.volumeCbm : null;
        while (entry.left > 0) {
            let load: SuggestedLoad = current ?? open();
            if (windowStart && ready && daysBetween(windowStart, ready) > windowDays && load.units > 0) {
                load = open();
            }
            let take = entry.left;
            if (opts.cap && unitCbm) {
                const room = opts.cap - load.cbm;
                const fits = Math.floor(room / unitCbm + 1e-9);
                if (fits <= 0 && load.units > 0) {
                    load = open();
                    continue;
                }
                take = Math.max(1, Math.min(entry.left, fits <= 0 ? 1 : fits));
            }
            load.qtys[entry.line.itemId] = (load.qtys[entry.line.itemId] ?? 0) + take;
            load.units += take;
            if (unitCbm) load.cbm += take * unitCbm;
            else if (!(entry.line.itemId in load.qtys) || load.qtys[entry.line.itemId] === take) load.uncounted += 1;
            if (ready && (!load.readyBy || ready > load.readyBy)) load.readyBy = ready;
            if (!windowStart && ready) windowStart = ready;
            entry.left -= take;
            if (opts.cap && unitCbm && load.cbm >= opts.cap - 1e-9) {
                current = null;
            }
        }
    }

    for (const load of loads) {
        load.cbm = Math.round(load.cbm * 100) / 100;
        if (opts.cap && opts.minFillPct && load.cbm < (opts.cap * opts.minFillPct) / 100) {
            load.underFilled = true;
            const short = Math.round((opts.cap - load.cbm) * 100) / 100;
            load.nudge = `${load.cbm} of ${opts.cap} CBM. ${short} CBM short of a full container.`;
        } else if (opts.cap) {
            load.nudge = `${load.cbm} of ${opts.cap} CBM`;
        }
    }
    return loads;
};

/** The latest ready date among the lines carrying a quantity, and which line sets it. */
export const readinessFloor = (
    lines: MotherOrderLine[],
    qtys: Record<string, number | ''>,
): { date: string; line: MotherOrderLine } | null => {
    let floor: { date: string; line: MotherOrderLine } | null = null;
    for (const line of lines) {
        if (!(Number(qtys[line.itemId]) > 0) || !line.expectedReadinessDate) continue;
        if (!floor || line.expectedReadinessDate > floor.date) {
            floor = { date: line.expectedReadinessDate, line };
        }
    }
    return floor;
};

export { addDaysIso };
